import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Store,
  Users,
  FileCheck,
  Bell,
  BarChart3,
  Settings,
  FileText,
  ChevronDown,
  ChevronRight,
  UserPlus,
  List,
  Clock,
  CheckCircle,
  XCircle,
  Megaphone,
  Mail,
  TrendingUp,
  PieChart,
  Shield,
  DollarSign,
  FolderOpen,
  Webhook,
  Info,
  CreditCard,
  Scale,
  Wallet,
  ClipboardCheck,
  Activity,
  Eye,
  History,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const SECTION_IDS = {
  MERCHANT: 'merchant',
  USERS: 'users',
  KYC: 'kyc',
  KYC_CO: 'kyc_co',
  NOTIFICATIONS: 'notifications',
  ANALYTICS: 'analytics',
  CONFIG: 'config',
  FINANCE: 'finance',
};

const getSectionIdForPath = (pathname) => {
  if (pathname.startsWith('/backoffice/merchants')) return SECTION_IDS.MERCHANT;
  if (pathname.startsWith('/backoffice/users')) return SECTION_IDS.USERS;
  if (pathname.startsWith('/backoffice/kyc')) return SECTION_IDS.KYC;
  if (pathname.startsWith('/backoffice/compliance')) return SECTION_IDS.KYC_CO;
  if (pathname.startsWith('/backoffice/notifications')) return SECTION_IDS.NOTIFICATIONS;
  if (pathname.startsWith('/backoffice/analytics')) return SECTION_IDS.ANALYTICS;
  if (pathname.startsWith('/backoffice/config')) return SECTION_IDS.CONFIG;
  if (pathname.startsWith('/backoffice/finance')) return SECTION_IDS.FINANCE;
  return null;
};

const NavSection = ({ title, icon: Icon, children }) => (
  <div className="mb-4 shrink-0">
    <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#0E1B16]/60">
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      {title}
    </div>
    <ul className="space-y-0.5">{children}</ul>
  </div>
);

const NavItem = ({ to, icon: Icon, label, end = false }) => (
  <li className="shrink-0">
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive ? 'bg-[#0B6445] text-white' : 'text-[#0E1B16] hover:bg-[#E6F7F3] hover:text-[#073B2E]'
        )
      }
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="truncate">{label}</span>
    </NavLink>
  </li>
);

const CollapsibleSection = ({ id, title, icon: Icon, isOpen, isChildActive, onToggle, children }) => {
  const handleClick = (e) => {
    e.preventDefault();
    onToggle(id);
  };
  return (
    <div className="mb-4 shrink-0">
      <button
        type="button"
        onClick={handleClick}
        className={cn(
          'flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
          isChildActive ? 'bg-[#0B6445]/10 text-[#073B2E]' : 'text-[#0E1B16] hover:bg-[#E6F7F3] hover:text-[#073B2E]'
        )}
      >
        <span className="flex items-center gap-3 min-w-0">
          {Icon && <Icon className="w-4 h-4 shrink-0" />}
          <span className="truncate">{title}</span>
        </span>
        {isOpen ? <ChevronDown className="w-4 h-4 shrink-0 ml-1" /> : <ChevronRight className="w-4 h-4 shrink-0 ml-1" />}
      </button>
      {isOpen && <ul className="mt-1 ml-4 space-y-0.5 border-l border-[#DCE4E0] pl-3">{children}</ul>}
    </div>
  );
};

const BackOfficeSidebar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const activeSectionId = useMemo(() => getSectionIdForPath(location.pathname), [location.pathname]);
  const [openSectionId, setOpenSectionId] = useState(null);

  useEffect(() => {
    if (activeSectionId) setOpenSectionId(activeSectionId);
  }, [activeSectionId]);

  const handleSectionToggle = (id) => {
    setOpenSectionId((prev) => (prev === id ? null : id));
  };

  const childPathsFor = (paths) => (pathname) =>
    paths.some((path) => pathname === path || (path !== '/backoffice/dashboard' && pathname.startsWith(path)));

  const role = user?.role || '';
  const perms = user?.permissions || [];
  const allAccess = new Set([role, ...perms]);

  const isSU = role === 'admin';
  const hasCO = isSU || allAccess.has('compliance_officer');
  const hasAccounts = isSU || allAccess.has('accounts');
  const hasOps = isSU || allAccess.has('operations');

  const roleLabel = isSU ? 'Super Admin' : role === 'compliance_officer' ? 'Compliance Officer'
    : role === 'accounts' ? 'Accounts' : role === 'operations' ? 'Operations' : role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const permLabels = perms.filter(p => p !== role).map(p => p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));

  return (
    <aside className="w-64 h-[calc(100vh-4rem)] fixed left-0 top-16 flex flex-col bg-white border-r border-[#DCE4E0] shrink-0">
      {/* Role badge */}
      <div className="px-4 pt-4 pb-2 border-b border-[#DCE4E0]">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#0B6445]" />
          <span className="text-xs font-semibold text-[#0B6445] uppercase tracking-wide">{roleLabel}</span>
        </div>
        {permLabels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {permLabels.map(p => (
              <span key={p} className="text-[10px] bg-[#E6F7F3] text-[#073B2E] px-1.5 py-0.5 rounded font-medium">{p}</span>
            ))}
          </div>
        )}
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 pb-8" style={{ scrollbarGutter: 'stable' }}>
        <NavItem to="/backoffice/dashboard" icon={LayoutDashboard} label="Dashboard Home" />

        {/* OPERATIONS: Merchant Management */}
        {hasOps && (
          <CollapsibleSection
            id={SECTION_IDS.MERCHANT}
            title="Merchant Management"
            icon={Store}
            isOpen={openSectionId === SECTION_IDS.MERCHANT}
            isChildActive={childPathsFor(['/backoffice/merchants', '/backoffice/kyc/pending', '/backoffice/kyc/rejected'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            <NavItem to="/backoffice/merchants" icon={List} label="All Merchants" end />
            {(isSU || hasOps) && <NavItem to="/backoffice/merchants/add" icon={UserPlus} label="Add New Merchant" />}
            <NavItem to="/backoffice/merchants/pending" icon={Clock} label="Pending Approval Queue" />
            <NavItem to="/backoffice/kyc/pending" icon={FileCheck} label="KYC Pending Review" />
            <NavItem to="/backoffice/kyc/rejected" icon={XCircle} label="Rejected KYC" />
            <NavItem to="/backoffice/merchants/suspended" icon={XCircle} label="Suspended Merchants" />
          </CollapsibleSection>
        )}

        {/* OPERATIONS: User Management */}
        {hasOps && (
          <CollapsibleSection
            id={SECTION_IDS.USERS}
            title="User Management"
            icon={Users}
            isOpen={openSectionId === SECTION_IDS.USERS}
            isChildActive={childPathsFor(['/backoffice/users'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            <NavItem to="/backoffice/users" icon={List} label="All Users" end />
            <NavItem to="/backoffice/users/invite" icon={UserPlus} label="Add / Invite User" />
            {isSU && <NavItem to="/backoffice/users/roles" icon={Shield} label="Roles & Permissions" />}
          </CollapsibleSection>
        )}

        {/* KYC/KYB Review — SU direct approval */}
        {isSU && (
          <CollapsibleSection
            id={SECTION_IDS.KYC}
            title="KYC/KYB Review"
            icon={FileCheck}
            isOpen={openSectionId === SECTION_IDS.KYC}
            isChildActive={childPathsFor(['/backoffice/kyc'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            <NavItem to="/backoffice/kyc" icon={List} label="All Applications" end />
            <NavItem to="/backoffice/kyc/pending" icon={Clock} label="Pending Review" />
            <NavItem to="/backoffice/kyc/approved" icon={CheckCircle} label="Approved" />
            <NavItem to="/backoffice/kyc/rejected" icon={XCircle} label="Rejected / On Hold" />
          </CollapsibleSection>
        )}

        {/* COMPLIANCE OFFICER: KYC Review Queue */}
        {hasCO && (
          <CollapsibleSection
            id={SECTION_IDS.KYC_CO}
            title={isSU ? "CO Review Management" : "KYC Review"}
            icon={ClipboardCheck}
            isOpen={openSectionId === SECTION_IDS.KYC_CO}
            isChildActive={childPathsFor(['/backoffice/compliance'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            {!isSU && <NavItem to="/backoffice/compliance/my-queue" icon={Clock} label="My Queue" />}
            {isSU && <NavItem to="/backoffice/compliance/all-reviews" icon={List} label="All CO Reviews" />}
            <NavItem to="/backoffice/compliance/review-history" icon={History} label="Review History" />
            <NavItem to="/backoffice/compliance/info-requests" icon={FileText} label="Info Requests" />
            <NavItem to="/backoffice/compliance/performance" icon={Activity} label={isSU ? "CO Performance" : "My Performance"} />
          </CollapsibleSection>
        )}

        {/* ACCOUNTS: Financial Management */}
        {hasAccounts && (
          <CollapsibleSection
            id={SECTION_IDS.FINANCE}
            title="Financial Management"
            icon={Wallet}
            isOpen={openSectionId === SECTION_IDS.FINANCE}
            isChildActive={childPathsFor(['/backoffice/finance'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            <NavItem to="/backoffice/finance/overview" icon={DollarSign} label="Ledger Overview" />
            <NavItem to="/backoffice/finance/reconciliation" icon={Scale} label="Reconciliation" />
            <NavItem to="/backoffice/finance/merchants" icon={Eye} label="Merchant Accounts" end />
            <NavItem to="/backoffice/finance/transactions" icon={List} label="All Transactions" />
          </CollapsibleSection>
        )}

        {/* Notifications (SU + CO + Operations) */}
        {(isSU || hasCO || hasOps) && (
          <CollapsibleSection
            id={SECTION_IDS.NOTIFICATIONS}
            title="Notifications"
            icon={Bell}
            isOpen={openSectionId === SECTION_IDS.NOTIFICATIONS}
            isChildActive={childPathsFor(['/backoffice/notifications'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            <NavItem to="/backoffice/notifications/log" icon={List} label="Notification Log" />
            {isSU && <NavItem to="/backoffice/notifications/broadcast" icon={Megaphone} label="Broadcast Message" />}
            {isSU && <NavItem to="/backoffice/notifications/templates" icon={Mail} label="Email Templates" />}
          </CollapsibleSection>
        )}

        {/* Analytics (everyone sees relevant subset) */}
        <CollapsibleSection
          id={SECTION_IDS.ANALYTICS}
          title="Analytics & Reports"
          icon={BarChart3}
          isOpen={openSectionId === SECTION_IDS.ANALYTICS}
          isChildActive={childPathsFor(['/backoffice/analytics'])(location.pathname)}
          onToggle={handleSectionToggle}
        >
          {(isSU || hasOps || hasAccounts) && <NavItem to="/backoffice/analytics/merchant-growth" icon={TrendingUp} label="Merchant Growth" />}
          {(isSU || hasCO || hasOps) && <NavItem to="/backoffice/analytics/kyc-funnel" icon={PieChart} label="KYC Funnel" />}
          {(isSU || hasCO) && <NavItem to="/backoffice/analytics/compliance" icon={FileCheck} label="Compliance Summary" />}
        </CollapsibleSection>

        {/* System Config (SU only) */}
        {isSU && (
          <CollapsibleSection
            id={SECTION_IDS.CONFIG}
            title="System Configuration"
            icon={Settings}
            isOpen={openSectionId === SECTION_IDS.CONFIG}
            isChildActive={childPathsFor(['/backoffice/config'])(location.pathname)}
            onToggle={handleSectionToggle}
          >
            <NavItem to="/backoffice/config/general" icon={Settings} label="General Settings" />
            <NavItem to="/backoffice/config/pg" icon={CreditCard} label="PG Config" />
            <NavItem to="/backoffice/config/fees" icon={DollarSign} label="Fee & Pricing Rules" />
            <NavItem to="/backoffice/config/docs" icon={FolderOpen} label="Document Requirements" />
            <NavItem to="/backoffice/config/integration" icon={Webhook} label="Integration Settings" />
          </CollapsibleSection>
        )}

        {/* Audit Logs */}
        {(isSU || hasCO || hasAccounts) && (
          <NavSection title="Audit" icon={FileText}>
            <NavItem to="/backoffice/audit-logs" icon={FileText} label={isSU ? "All System Events" : "My Activity Log"} />
          </NavSection>
        )}

        {isSU && (
          <NavSection title="Reference" icon={Info}>
            <NavItem to="/backoffice/info" icon={Info} label="API Info" />
          </NavSection>
        )}
      </nav>
    </aside>
  );
};

export default BackOfficeSidebar;
